import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import Select from 'react-select';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useSubscriberAnalytics } from '../services/api/hooks/useSubscriberAnalytics';
import { REASON_LABELS, getActiveReasons, hasAnyReasonData } from '../utils/unsubscribeReasons';
import { formatDate } from '../utils/dateFormatter';


ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface SubscriberAnalyticsProps {
  days?: number;
  startDate?: string;
  endDate?: string;
  trigger?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  topicId?: number;
}

const SubscriberAnalytics: React.FC<SubscriberAnalyticsProps> = ({ days = 30, startDate = '', endDate = '', trigger, onLoadingChange, topicId }) => {
  const [selectedTopicId, setSelectedTopicId] = useState<number | undefined>(topicId);
  
  // Sync internal state with prop when topicId changes
  useEffect(() => {
    setSelectedTopicId(topicId);
  }, [topicId]);
  
  const { data, snapshotData, isLoading, error, fetchData, fetchSnapshotData, downloadAnalyticsCsv, downloadDailyCsv } = useSubscriberAnalytics(days, startDate, endDate, selectedTopicId || undefined);

  const [downloadingType, setDownloadingType] = useState<string | null>(null);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [showDetailedBreakdown, setShowDetailedBreakdown] = useState(false);
  const detailsRef = React.useRef<HTMLDivElement>(null);




  // Auto-select top 5 sources by count when data changes
  useEffect(() => {
    if (data?.sourceBreakdown) {
      const sourceCounts = data.sourceBreakdown.reduce((acc, item) => {
        acc[item.source] = (acc[item.source] || 0) + item.count;
        return acc;
      }, {} as Record<string, number>);
      
      const topSources = Object.entries(sourceCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([source]) => source);
      
      setSelectedSources(topSources);
    }
  }, [data?.sourceBreakdown]);



  const downloadCsv = async (type: 'subscriptions' | 'unsubscriptions', date?: string) => {
    if (!data) return;
    
    const downloadKey = date ? `${type}-${date}` : type;
    setDownloadingType(downloadKey);
    
    try {
      if (date) {
        await downloadDailyCsv(type, date);
      } else {
        if (startDate && endDate) {
          await downloadAnalyticsCsv(type, 0, startDate, endDate);
        } else {
          await downloadAnalyticsCsv(type, days);
        }
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to download CSV');
    } finally {
      setDownloadingType(null);
    }
  };

  React.useEffect(() => {
    if (trigger) {
      fetchData();
      fetchSnapshotData();
    }
  }, [trigger, fetchData, fetchSnapshotData]);



  React.useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading analytics</div>;
  if (!data) return <div>Click "Generate Report" to view subscriber trends</div>;

  // Prepare chart data
  const dates = Array.from(new Set([
    ...(data?.subscriptions.map(s => s.date) || []),
    ...(data?.unsubscriptions.map(u => u.date) || [])
  ])).sort();

  const subscriptionData = dates.map(date => {
    const found = data?.subscriptions.find(s => s.date === date);
    return found ? found.count : 0;
  });

  const unsubscriptionData = dates.map(date => {
    const found = data?.unsubscriptions.find(u => u.date === date);
    return found ? found.count : 0;
  });

  const chartData = {
    labels: dates.map(date => formatDate(date)),
    datasets: [
      {
        label: 'New Subscriptions',
        data: subscriptionData,
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.2)',
        tension: 0.1,
      },
      {
        label: 'Unsubscriptions',
        data: unsubscriptionData,
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.2)',
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Daily Subscriber Activity',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <div style={{ overflowX: 'hidden' }}>
      {data && (
        <>
          <div className="row">
            <div className="col-md-8">
              <div className="bg-white p-3 border rounded">
                <Line data={chartData} options={options} />
              </div>
            </div>
            <div className="col-md-4">
              <div className="row mb-3">
                <div className="col-6">
                  <div className="card border-success" style={{ cursor: 'pointer' }} onClick={() => downloadCsv('subscriptions')}>
                    <div className="card-body text-center">
                      <h6 className="card-title text-success">New Subscriptions</h6>
                      <h4 className="text-success">
                        {downloadingType === 'subscriptions' ? (
                          <div className="spinner-border spinner-border-sm text-success" role="status"></div>
                        ) : (
                          subscriptionData.reduce((a, b) => a + b, 0)
                        )}
                      </h4>
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="card border-danger" style={{ cursor: 'pointer' }} onClick={() => downloadCsv('unsubscriptions')}>
                    <div className="card-body text-center">
                      <h6 className="card-title text-danger">Unsubscriptions</h6>
                      <h4 className="text-danger">
                        {downloadingType === 'unsubscriptions' ? (
                          <div className="spinner-border spinner-border-sm text-danger" role="status"></div>
                        ) : (
                          unsubscriptionData.reduce((a, b) => a + b, 0)
                        )}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="row mt-3">
            <div className="col-12">
              {/* Source Selection */}
              <div className="card mb-3">
                <div className="card-body">
                  <label className="form-label mb-2">Select Sources to Display:</label>
                  <small className="text-muted mb-2 d-block">Choose up to 5 sources to show as individual columns in the breakdown table. Unselected sources will be grouped under "Other" column.</small>
                  <div className="input-group">
                    <Select
                      isMulti
                      name="selectedSources"
                      options={(() => {
                        const sourceCounts = data?.sourceBreakdown?.reduce((acc, item) => {
                          acc[item.source] = (acc[item.source] || 0) + item.count;
                          return acc;
                        }, {} as Record<string, number>) || {};
                        
                        return Object.entries(sourceCounts)
                          .sort(([,a], [,b]) => b - a)
                          .map(([source, count]) => ({ 
                            value: source, 
                            label: `${source} (${count})` 
                          }));
                      })()}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      value={selectedSources.map(source => {
                        const sourceCounts = data?.sourceBreakdown?.reduce((acc, item) => {
                          acc[item.source] = (acc[item.source] || 0) + item.count;
                          return acc;
                        }, {} as Record<string, number>) || {};
                        return { value: source, label: `${source} (${sourceCounts[source] || 0})` };
                      })}
                      onChange={(selectedOptions) => {
                        const newSources = selectedOptions ? selectedOptions.map(option => option.value) : [];
                        if (newSources.length <= 5) {
                          setSelectedSources(newSources);
                        }
                      }}
                      isOptionDisabled={() => selectedSources.length >= 5}
                      placeholder="Select sources to display..."
                      styles={{ 
                        container: (provided) => ({ ...provided, flex: 1 }),
                        menu: (provided) => ({ ...provided, zIndex: 9999 })
                      }}
                    />
                    <button 
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => {
                        const allSources = Array.from(new Set(
                          data?.sourceBreakdown?.map(s => s.source) || []
                        ));
                        setSelectedSources(allSources.slice(0, 5));
                      }}
                    >
                      Select All
                    </button>
                    <button 
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setSelectedSources([])}
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Unsubscribe Reason Breakdown */}
          {data?.reasonBreakdown && data.reasonBreakdown.length > 0 && hasAnyReasonData(data.reasonBreakdown) && (
            <div className="row mt-3">
              <div className="col-12">
                {/* Simplified Bounce vs Others Breakdown */}
                <div className="card mb-3">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Unsubscribe Reasons Breakdown</h6>
                    <div>
                      <button 
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => {
                          setShowDetailedBreakdown(!showDetailedBreakdown);
                          if (!showDetailedBreakdown) {
                            setTimeout(() => detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                          }
                        }}
                      >
                        {showDetailedBreakdown ? 'Hide Details' : 'Fetch Details'}
                      </button>
                      <button 
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                        const displayReasonSources = [...selectedSources, 'Other'];
                        const header1 = ['Date', 'Bounce', '', '', '', 'Others', '', '', ''];
                        const header2 = ['', ...displayReasonSources, 'Total', ...displayReasonSources, 'Total'];
                        
                        const rows = dates.slice().reverse().map((date) => {
                          const reasonsForDate = data.reasonBreakdown.filter(r => r.date === date);
                          const row = [date];
                          
                          // Bounce counts
                          let bounceTotal = 0;
                          selectedSources.forEach(source => {
                            const count = reasonsForDate.find(r => r.reason === 'bounce' && r.source === source)?.count || 0;
                            row.push(count.toString());
                            bounceTotal += count;
                          });
                          const bounceOther = reasonsForDate.filter(r => r.reason === 'bounce' && !selectedSources.includes(r.source)).reduce((sum, r) => sum + r.count, 0);
                          row.push(bounceOther.toString());
                          bounceTotal += bounceOther;
                          row.push(bounceTotal.toString());
                          
                          // Others counts
                          let othersTotal = 0;
                          selectedSources.forEach(source => {
                            const count = reasonsForDate.filter(r => r.reason !== 'bounce' && r.source === source).reduce((sum, r) => sum + r.count, 0);
                            row.push(count.toString());
                            othersTotal += count;
                          });
                          const othersOther = reasonsForDate.filter(r => r.reason !== 'bounce' && !selectedSources.includes(r.source)).reduce((sum, r) => sum + r.count, 0);
                          row.push(othersOther.toString());
                          othersTotal += othersOther;
                          row.push(othersTotal.toString());
                          
                          return row;
                        });
                        
                        const totalRow = ['Total'];
                        let bounceTotalAll = 0;
                        selectedSources.forEach(source => {
                          const total = data.reasonBreakdown.filter(r => r.reason === 'bounce' && r.source === source).reduce((sum, r) => sum + r.count, 0);
                          totalRow.push(total.toString());
                          bounceTotalAll += total;
                        });
                        const bounceOtherAll = data.reasonBreakdown.filter(r => r.reason === 'bounce' && !selectedSources.includes(r.source)).reduce((sum, r) => sum + r.count, 0);
                        totalRow.push(bounceOtherAll.toString());
                        bounceTotalAll += bounceOtherAll;
                        totalRow.push(bounceTotalAll.toString());
                        
                        let othersTotalAll = 0;
                        selectedSources.forEach(source => {
                          const total = data.reasonBreakdown.filter(r => r.reason !== 'bounce' && r.source === source).reduce((sum, r) => sum + r.count, 0);
                          totalRow.push(total.toString());
                          othersTotalAll += total;
                        });
                        const othersOtherAll = data.reasonBreakdown.filter(r => r.reason !== 'bounce' && !selectedSources.includes(r.source)).reduce((sum, r) => sum + r.count, 0);
                        totalRow.push(othersOtherAll.toString());
                        othersTotalAll += othersOtherAll;
                        totalRow.push(othersTotalAll.toString());
                        
                        const csvContent = [header1, header2, ...rows, totalRow].map(row => row.join('\t')).join('\n');
                        navigator.clipboard.writeText(csvContent);
                        alert('Table copied to clipboard!');
                      }}
                        title="Copy table data"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto', position: 'relative' }}>
                      {(() => {
                        const displayReasonSources = [...selectedSources, 'Other'];
                        
                        return (
                          <table className="table table-sm table-striped">
                            <thead className="sticky-top bg-white" style={{ top: 0, zIndex: 2 }}>
                              <tr>
                                <th style={{ fontSize: '0.875rem' }}>Date</th>
                                <th colSpan={displayReasonSources.length + 1} className="text-center" style={{ color: '#e74c3c', fontSize: '0.875rem' }}>Bounce</th>
                                <th colSpan={displayReasonSources.length + 1} className="text-center" style={{ color: '#f39c12', fontSize: '0.875rem' }}>Others</th>
                                <th rowSpan={2} className="text-center" style={{ color: '#6c757d', fontSize: '0.875rem' }}>Grand Total</th>
                              </tr>
                              <tr>
                                <th></th>
                                {displayReasonSources.map(source => (
                                  <th key={`bounce-${source}`} className="text-end" style={{ fontSize: '0.75rem', minWidth: '50px', maxWidth: '80px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#e74c3c' }} title={source}>
                                    {source}
                                  </th>
                                ))}
                                <th className="text-end" style={{ color: '#e74c3c', fontSize: '0.75rem' }}>Total</th>
                                {displayReasonSources.map(source => (
                                  <th key={`others-${source}`} className="text-end" style={{ fontSize: '0.75rem', minWidth: '50px', maxWidth: '80px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#f39c12' }} title={source}>
                                    {source}
                                  </th>
                                ))}
                                <th className="text-end" style={{ color: '#f39c12', fontSize: '0.75rem' }}>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dates.slice().reverse().map((date) => {
                                const reasonsForDate = data.reasonBreakdown.filter(r => r.date === date);
                                
                                return (
                                  <tr key={date}>
                                    <td style={{ fontSize: '0.875rem' }}>{formatDate(date)}</td>
                                    {/* Bounce columns */}
                                    {selectedSources.map(source => {
                                      const count = reasonsForDate.find(r => r.reason === 'bounce' && r.source === source)?.count || 0;
                                      return (
                                        <td key={`bounce-${source}-${date}`} className="text-end" style={{ fontSize: '0.875rem', color: '#e74c3c' }}>
                                          {count}
                                        </td>
                                      );
                                    })}
                                    <td className="text-end" style={{ fontSize: '0.875rem', color: '#e74c3c' }}>
                                      {reasonsForDate.filter(r => r.reason === 'bounce' && !selectedSources.includes(r.source)).reduce((sum, r) => sum + r.count, 0)}
                                    </td>
                                    <td className="text-end fw-bold" style={{ color: '#e74c3c', fontSize: '0.875rem' }}>
                                      {reasonsForDate.filter(r => r.reason === 'bounce').reduce((sum, r) => sum + r.count, 0)}
                                    </td>
                                    {/* Others columns */}
                                    {selectedSources.map(source => {
                                      const count = reasonsForDate.filter(r => r.reason !== 'bounce' && r.source === source).reduce((sum, r) => sum + r.count, 0);
                                      return (
                                        <td key={`others-${source}-${date}`} className="text-end" style={{ fontSize: '0.875rem', color: '#f39c12' }}>
                                          {count}
                                        </td>
                                      );
                                    })}
                                    <td className="text-end" style={{ fontSize: '0.875rem', color: '#f39c12' }}>
                                      {reasonsForDate.filter(r => r.reason !== 'bounce' && !selectedSources.includes(r.source)).reduce((sum, r) => sum + r.count, 0)}
                                    </td>
                                    <td className="text-end fw-bold" style={{ color: '#f39c12', fontSize: '0.875rem' }}>
                                      {reasonsForDate.filter(r => r.reason !== 'bounce').reduce((sum, r) => sum + r.count, 0)}
                                    </td>
                                    <td className="text-end fw-bold" style={{ color: '#6c757d', fontSize: '0.875rem' }}>
                                      {reasonsForDate.reduce((sum, r) => sum + r.count, 0)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot className="bg-light" style={{ position: 'sticky', bottom: 0, zIndex: 2 }}>
                              <tr>
                                <th style={{ fontSize: '0.875rem' }}>Total</th>
                                {/* Bounce totals */}
                                {selectedSources.map(source => {
                                  const total = data.reasonBreakdown.filter(r => r.reason === 'bounce' && r.source === source).reduce((sum, r) => sum + r.count, 0);
                                  return (
                                    <th key={`total-bounce-${source}`} className="text-end" style={{ color: '#e74c3c', fontSize: '0.875rem' }}>
                                      {total}
                                    </th>
                                  );
                                })}
                                <th className="text-end" style={{ color: '#e74c3c', fontSize: '0.875rem' }}>
                                  {data.reasonBreakdown.filter(r => r.reason === 'bounce' && !selectedSources.includes(r.source)).reduce((sum, r) => sum + r.count, 0)}
                                </th>
                                <th className="text-end fw-bold" style={{ color: '#e74c3c', fontSize: '0.875rem' }}>
                                  {data.reasonBreakdown.filter(r => r.reason === 'bounce').reduce((sum, r) => sum + r.count, 0)}
                                </th>
                                {/* Others totals */}
                                {selectedSources.map(source => {
                                  const total = data.reasonBreakdown.filter(r => r.reason !== 'bounce' && r.source === source).reduce((sum, r) => sum + r.count, 0);
                                  return (
                                    <th key={`total-others-${source}`} className="text-end" style={{ color: '#f39c12', fontSize: '0.875rem' }}>
                                      {total}
                                    </th>
                                  );
                                })}
                                <th className="text-end" style={{ color: '#f39c12', fontSize: '0.875rem' }}>
                                  {data.reasonBreakdown.filter(r => r.reason !== 'bounce' && !selectedSources.includes(r.source)).reduce((sum, r) => sum + r.count, 0)}
                                </th>
                                <th className="text-end fw-bold" style={{ color: '#f39c12', fontSize: '0.875rem' }}>
                                  {data.reasonBreakdown.filter(r => r.reason !== 'bounce').reduce((sum, r) => sum + r.count, 0)}
                                </th>
                                <th className="text-end fw-bold" style={{ color: '#6c757d', fontSize: '0.875rem' }}>
                                  {data.reasonBreakdown.reduce((sum, r) => sum + r.count, 0)}
                                </th>
                              </tr>
                            </tfoot>
                          </table>
                        );
                      })()} 
                    </div>
                  </div>
                </div>
                
                {showDetailedBreakdown && (
                  <>
                    {/* Reason Explanation */}
                    <div className="card mb-3" ref={detailsRef}>
                  <div className="card-body">
                    <label className="form-label mb-2">Unsubscribe Reason Types:</label>
                    <small className="text-muted mb-2 d-block">
                      <strong>User Click:</strong> Unsubscribed via email links | 
                      <strong>One-Click Unsubscribe:</strong> One-click unsubscribe via email client | 
                      <strong>Bounce:</strong> Bulk opt-outs/delivery failures | 
                      <strong>Debounce API:</strong> Invalid emails from validation | 
                      <strong>Manual API:</strong> Admin actions | 
                      <strong>Upload CSV:</strong> Imported unsubscribed users | 
                      <strong>FBL Complaint:</strong> Spam complaints from email providers | 
                      <strong>OOB Bounce:</strong> Out-of-band hard bounces from mail servers
                    </small>
                  </div>
                </div>
                
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Unsubscribe Reasons Breakdown</h6>
                    <button 
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        const reasons = getActiveReasons(data.reasonBreakdown);
                        const displayReasonSources = [...selectedSources, 'Other'];
                        
                        const header1 = ['Date'];
                        reasons.forEach(reason => {
                          header1.push(REASON_LABELS[reason as keyof typeof REASON_LABELS]);
                          for (let i = 1; i < displayReasonSources.length + 1; i++) {
                            header1.push('');
                          }
                        });
                        header1.push('Total');
                        
                        const header2 = [''];
                        reasons.forEach(reason => {
                          displayReasonSources.forEach(source => {
                            header2.push(source);
                          });
                          header2.push('Total');
                        });
                        header2.push('Grand');
                        
                        const rows = dates.slice().reverse().map((date) => {
                          const reasonsForDate = data.reasonBreakdown.filter(r => r.date === date);
                          const row = [date];
                          let grandTotal = 0;
                          
                          reasons.forEach(reason => {
                            let reasonTotal = 0;
                            selectedSources.forEach(source => {
                              const count = reasonsForDate.find(r => r.reason === reason && r.source === source)?.count || 0;
                              row.push(count.toString());
                              reasonTotal += count;
                            });
                            const otherCount = reasonsForDate.filter(r => r.reason === reason && !selectedSources.includes(r.source)).reduce((sum, r) => sum + r.count, 0);
                            row.push(otherCount.toString());
                            reasonTotal += otherCount;
                            row.push(reasonTotal.toString());
                            grandTotal += reasonTotal;
                          });
                          
                          row.push(grandTotal.toString());
                          return row;
                        });
                        const totalRow = ['Total'];
                        let overallGrandTotal = 0;
                        reasons.forEach(reason => {
                          let reasonGrandTotal = 0;
                          selectedSources.forEach(source => {
                            const total = data.reasonBreakdown.filter(r => r.reason === reason && r.source === source).reduce((sum, r) => sum + r.count, 0);
                            totalRow.push(total.toString());
                            reasonGrandTotal += total;
                          });
                          const otherTotal = data.reasonBreakdown.filter(r => r.reason === reason && !selectedSources.includes(r.source)).reduce((sum, r) => sum + r.count, 0);
                          totalRow.push(otherTotal.toString());
                          reasonGrandTotal += otherTotal;
                          totalRow.push(reasonGrandTotal.toString());
                          overallGrandTotal += reasonGrandTotal;
                        });
                        totalRow.push(overallGrandTotal.toString());
                        const csvContent = [header1, header2, ...rows, totalRow].map(row => row.join('\t')).join('\n');
                        navigator.clipboard.writeText(csvContent);
                        alert('Table copied to clipboard!');
                      }}
                      title="Copy table data"
                    >
                      Copy
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto', position: 'relative' }}>
                      {(() => {
                        const reasons = getActiveReasons(data.reasonBreakdown);
                        const displayReasonSources = [...selectedSources, 'Other'];
                        const reasonColors: Record<string, string> = {
                          'user_click': '#e57373',
                          'one_click_unsubscribe': '#ef5350',
                          'bounce': '#f44336',
                          'debounce_api': '#e91e63',
                          'manual_api': '#ff5252',
                          'upload_csv': '#ff6e40'
                        };
                        
                        return (
                          <table className="table table-sm table-striped">
                            <thead className="sticky-top bg-white" style={{ top: 0, zIndex: 2 }}>
                              <tr>
                                <th style={{ fontSize: '0.875rem' }}>Date</th>
                                {reasons.map(reason => (
                                  <th key={reason} colSpan={displayReasonSources.length + 1} className="text-center" style={{ color: reasonColors[reason], fontSize: '0.875rem' }}>{REASON_LABELS[reason as keyof typeof REASON_LABELS]}</th>
                                ))}
                                <th className="text-danger text-center" style={{ fontSize: '0.875rem' }}>Total</th>
                              </tr>
                              <tr>
                                <th></th>
                                {reasons.map(reason => [
                                  ...displayReasonSources.map(source => (
                                    <th key={`${reason}-${source}`} className="text-end" style={{ fontSize: '0.75rem', minWidth: '50px', maxWidth: '80px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: reasonColors[reason] }} title={source}>
                                      {source}
                                    </th>
                                  )),
                                  <th key={`${reason}-total`} className="text-end" style={{ color: reasonColors[reason], fontSize: '0.75rem' }}>Total</th>
                                ])}
                                <th className="text-end" style={{ color: '#6c757d', fontSize: '0.75rem' }}>Grand</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dates.slice().reverse().map((date, index) => {
                                const reasonsForDate = data.reasonBreakdown.filter(r => r.date === date);
                                let grandTotal = 0;
                                
                                return (
                                  <tr key={date}>
                                    <td style={{ fontSize: '0.875rem' }}>{formatDate(date)}</td>
                                    {reasons.map(reason => {
                                      let reasonTotal = 0;
                                      const cells = [];

                                      selectedSources.forEach(source => {
                                        const count = reasonsForDate.find(r => r.reason === reason && r.source === source)?.count || 0;
                                        cells.push(
                                          <td key={`${reason}-${source}-${date}`} className="text-end" style={{ fontSize: '0.875rem', color: reasonColors[reason] }}>
                                            {count}
                                          </td>
                                        );
                                        reasonTotal += count;
                                      });

                                      const otherCount = reasonsForDate.filter(r => r.reason === reason && !selectedSources.includes(r.source)).reduce((sum, r) => sum + r.count, 0);
                                      cells.push(
                                        <td key={`${reason}-other-${date}`} className="text-end" style={{ fontSize: '0.875rem', color: reasonColors[reason] }}>
                                          {otherCount}
                                        </td>
                                      );
                                      reasonTotal += otherCount;

                                      cells.push(
                                        <td key={`${reason}-total-${date}`} className="text-end fw-bold" style={{ color: reasonColors[reason], fontSize: '0.875rem' }}>
                                          {reasonTotal}
                                        </td>
                                      );

                                      grandTotal += reasonTotal;
                                      return cells;
                                    })}
                                    <td className="text-end fw-bold" style={{ color: '#6c757d', fontSize: '0.875rem' }}>{grandTotal}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot className="bg-light" style={{ position: 'sticky', bottom: 0, zIndex: 2 }}>
                              <tr>
                                <th style={{ fontSize: '0.875rem' }}>Total</th>
                                {reasons.map(reason => {
                                  const cells = [];
                                  let reasonGrandTotal = 0;

                                  selectedSources.forEach(source => {
                                    const total = data.reasonBreakdown.filter(r => r.reason === reason && r.source === source).reduce((sum, r) => sum + r.count, 0);
                                    cells.push(
                                      <th key={`total-${reason}-${source}`} className="text-end" style={{ color: reasonColors[reason], fontSize: '0.875rem' }}>
                                        {total}
                                      </th>
                                    );
                                    reasonGrandTotal += total;
                                  });

                                  const otherTotal = data.reasonBreakdown.filter(r => r.reason === reason && !selectedSources.includes(r.source)).reduce((sum, r) => sum + r.count, 0);
                                  cells.push(
                                    <th key={`total-${reason}-other`} className="text-end" style={{ color: reasonColors[reason], fontSize: '0.875rem' }}>
                                      {otherTotal}
                                    </th>
                                  );
                                  reasonGrandTotal += otherTotal;

                                  cells.push(
                                    <th key={`total-${reason}-total`} className="text-end fw-bold" style={{ color: reasonColors[reason], fontSize: '0.875rem' }}>
                                      {reasonGrandTotal}
                                    </th>
                                  );

                                  return cells;
                                })}
                                <th className="text-end fw-bold" style={{ color: '#6c757d', fontSize: '0.875rem' }}>
                                  {data.reasonBreakdown.reduce((sum, r) => sum + r.count, 0)}
                                </th>
                              </tr>
                            </tfoot>
                          </table>
                        );
                      })()} 
                    </div>
                  </div>
                </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Source Snapshot Report */}
          {snapshotData && snapshotData.length > 0 && (
            <div className="row mt-3">
              <div className="col-12">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Daily Subscriber Balance Report</h6>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        // Process snapshot data for CSV export
                        const allDates = Array.from(new Set(snapshotData.map(s => s.snapshotDate))).sort();
                        
                        const dates = (startDate && endDate && startDate.trim() && endDate.trim())
                          ? allDates.filter(date => date >= startDate && date <= endDate)
                          : allDates.slice(1); // Remove the extra fetched day when using days parameter
                        const allSources = Array.from(new Set(snapshotData.map(s => s.source))).sort();
                        const displaySources = [...selectedSources.filter(s => allSources.includes(s)), 'Other'];

                        // Build headers
                        const header1 = ['Date', 'Opening Balance'];
                        displaySources.forEach(source => {
                          header1.push(`OptIn - ${source}`);
                        });
                        header1.push('Total OptIns');
                        displaySources.forEach(source => {
                          header1.push(`OptOut - ${source}`);
                        });
                        header1.push('Total OptOuts');
                        header1.push('Final Total');

                        // Build rows
                        const rows = dates.map((date, dateIndex) => {
                          const snapshotsForDate = snapshotData.filter(s => s.snapshotDate === date);

                          // Opening balance = sum of optInCount from previous day (from allDates which includes the extra day)
                          let openingBalance = 0;
                          const dateIndexInAll = allDates.indexOf(date);
                          if (dateIndexInAll > 0) {
                            const previousDate = allDates[dateIndexInAll - 1];
                            const previousSnapshots = snapshotData.filter(s => s.snapshotDate === previousDate);
                            openingBalance = previousSnapshots.reduce((sum, s) => sum + s.optInCount, 0);
                          }

                          const row = [date, openingBalance.toString()];

                          // New Subscriptions columns (using newSubscriptions field)
                          let totalNewSubs = 0;
                          selectedSources.filter(s => allSources.includes(s)).forEach(source => {
                            const todaySnapshot = snapshotsForDate.find(s => s.source === source);
                            const newSubs = todaySnapshot?.newSubscriptions || 0;
                            row.push(newSubs.toString());
                            totalNewSubs += newSubs;
                          });
                          // Other New Subscriptions
                          const otherNewSubs = snapshotsForDate
                            .filter(s => !selectedSources.includes(s.source))
                            .reduce((sum, s) => sum + (s.newSubscriptions || 0), 0);
                          row.push(otherNewSubs.toString());
                          totalNewSubs += otherNewSubs;
                          row.push(totalNewSubs.toString());

                          // Unsubscriptions columns (using unsubscriptions field)
                          let totalUnsubs = 0;
                          selectedSources.filter(s => allSources.includes(s)).forEach(source => {
                            const todaySnapshot = snapshotsForDate.find(s => s.source === source);
                            const unsubs = todaySnapshot?.unsubscriptions || 0;
                            row.push(unsubs.toString());
                            totalUnsubs += unsubs;
                          });
                          // Other Unsubscriptions
                          const otherUnsubs = snapshotsForDate
                            .filter(s => !selectedSources.includes(s.source))
                            .reduce((sum, s) => sum + (s.unsubscriptions || 0), 0);
                          row.push(otherUnsubs.toString());
                          totalUnsubs += otherUnsubs;
                          row.push(totalUnsubs.toString());

                          // Final Total = Opening Balance + OptIns - OptOuts
                          const finalTotal = openingBalance + totalNewSubs - totalUnsubs;
                          row.push(finalTotal.toString());

                          return row;
                        });

                        const csvContent = [header1, ...rows].map(row => row.join('\t')).join('\n');
                        navigator.clipboard.writeText(csvContent);
                        alert('Table copied to clipboard!');
                      }}
                      title="Copy table data"
                    >
                      Copy
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto', position: 'relative' }}>
                      {(() => {
                        const allDates = Array.from(new Set(snapshotData.map(s => s.snapshotDate))).sort();
                        
                        const dates = (startDate && endDate && startDate.trim() && endDate.trim())
                          ? allDates.filter(date => date >= startDate && date <= endDate)
                          : allDates.slice(1); // Remove the extra fetched day when using days parameter
                        const allSources = Array.from(new Set(snapshotData.map(s => s.source))).sort();
                        const displaySources = [...selectedSources.filter(s => allSources.includes(s)), 'Other'];

                        return (
                          <table className="table table-sm table-striped">
                            <thead className="sticky-top bg-white" style={{ top: 0, zIndex: 2 }}>
                              <tr>
                                <th rowSpan={2} style={{ fontSize: '0.875rem' }}>Date</th>
                                <th rowSpan={2} style={{ fontSize: '0.875rem' }}>Opening Balance</th>
                                <th colSpan={displaySources.length + 1} className="text-success text-center" style={{ fontSize: '0.875rem' }}>OptIn</th>
                                <th colSpan={displaySources.length + 1} className="text-danger text-center" style={{ fontSize: '0.875rem' }}>OptOut</th>
                                <th rowSpan={2} style={{ fontSize: '0.875rem' }}>Final Total</th>
                              </tr>
                              <tr>
                                {displaySources.map(source => (
                                  <th key={`optin-${source}`} className="text-success text-end" style={{ fontSize: '0.75rem', minWidth: '50px', maxWidth: '80px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={source}>
                                    {source}
                                  </th>
                                ))}
                                <th className="text-success text-end fw-bold" style={{ fontSize: '0.75rem' }}>Total</th>
                                {displaySources.map(source => (
                                  <th key={`optout-${source}`} className="text-danger text-end" style={{ fontSize: '0.75rem', minWidth: '50px', maxWidth: '80px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={source}>
                                    {source}
                                  </th>
                                ))}
                                <th className="text-danger text-end fw-bold" style={{ fontSize: '0.75rem' }}>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dates.map((date, dateIndex) => {
                                const snapshotsForDate = snapshotData.filter(s => s.snapshotDate === date);

                                // Opening balance = sum of optInCount from previous day (from allDates which includes the extra day)
                                let openingBalance = 0;
                                const dateIndexInAll = allDates.indexOf(date);
                                if (dateIndexInAll > 0) {
                                  const previousDate = allDates[dateIndexInAll - 1];
                                  const previousSnapshots = snapshotData.filter(s => s.snapshotDate === previousDate);
                                  openingBalance = previousSnapshots.reduce((sum, s) => sum + s.optInCount, 0);
                                }

                                // Calculate New Subscriptions per source (using newSubscriptions field)
                                let totalNewSubs = 0;
                                const optInCells = selectedSources.filter(s => allSources.includes(s)).map(source => {
                                  const todaySnapshot = snapshotsForDate.find(s => s.source === source);
                                  const newSubs = todaySnapshot?.newSubscriptions || 0;
                                  totalNewSubs += newSubs;
                                  return (
                                    <td key={`optin-${source}-${date}`} className="text-success text-end" style={{ fontSize: '0.875rem' }}>
                                      {newSubs}
                                    </td>
                                  );
                                });

                                // Other New Subscriptions (sum of newSubscriptions for other sources)
                                const otherNewSubs = snapshotsForDate
                                  .filter(s => !selectedSources.includes(s.source))
                                  .reduce((sum, s) => sum + (s.newSubscriptions || 0), 0);
                                totalNewSubs += otherNewSubs;

                                // Calculate Unsubscriptions per source (using unsubscriptions field)
                                let totalUnsubs = 0;
                                const optOutCells = selectedSources.filter(s => allSources.includes(s)).map(source => {
                                  const todaySnapshot = snapshotsForDate.find(s => s.source === source);
                                  const unsubs = todaySnapshot?.unsubscriptions || 0;
                                  totalUnsubs += unsubs;
                                  return (
                                    <td key={`optout-${source}-${date}`} className="text-danger text-end" style={{ fontSize: '0.875rem' }}>
                                      {unsubs}
                                    </td>
                                  );
                                });

                                // Other Unsubscriptions (sum of unsubscriptions for other sources)
                                const otherUnsubs = snapshotsForDate
                                  .filter(s => !selectedSources.includes(s.source))
                                  .reduce((sum, s) => sum + (s.unsubscriptions || 0), 0);
                                totalUnsubs += otherUnsubs;

                                // Final Total = Opening Balance + OptIns - OptOuts
                                const finalTotal = openingBalance + totalNewSubs - totalUnsubs;

                                return (
                                  <tr key={date}>
                                    <td style={{ fontSize: '0.875rem' }}>{formatDate(date)}</td>
                                    <td className="text-end" style={{ fontSize: '0.875rem' }}>{openingBalance}</td>
                                    {optInCells}
                                    <td className="text-success text-end" style={{ fontSize: '0.875rem' }}>
                                      {otherNewSubs}
                                    </td>
                                    <td className="text-success text-end fw-bold" style={{ fontSize: '0.875rem' }}>
                                      {totalNewSubs}
                                    </td>
                                    {optOutCells}
                                    <td className="text-danger text-end" style={{ fontSize: '0.875rem' }}>
                                      {otherUnsubs}
                                    </td>
                                    <td className="text-danger text-end fw-bold" style={{ fontSize: '0.875rem' }}>
                                      {totalUnsubs}
                                    </td>
                                    <td className="text-end fw-bold" style={{ fontSize: '0.875rem' }}>{finalTotal}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SubscriberAnalytics;